import type { DefaultContext } from "@apollo/client";
import {
  ApolloClient,
  ApolloLink,
  createHttpLink,
  from,
  InMemoryCache,
  Observable,
  split
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { parse as parseCookie } from "cookie";
import cookie from "js-cookie";

import { isServer } from "@shared/utils.environment";

import { getUserSegment, setUserSegment } from "utils/userSegment";

/* eslint-disable import/no-cycle */
import { navigateTo } from "@shared/utils.routing";
import { anonymousLogin, refreshToken } from "./api";
import { clearUser, userVar } from "./user";
/* eslint-enable import/no-cycle */

import { getServerCookies } from "app/serverCookies";
import type { SessionCookie } from "session";
import { getExperienceId } from "utils/getExperienceId";
import { CSRF_TOKEN_COOKIE } from "./apollo/constants";
import { getInitialAnonId } from "./apollo/initialvals";
import { containsSomeOfErrors, getErrorCodes } from "./apolloErrors";
import { getBPFingerprint } from "./fingerprint";
import { setNotifications } from "./notifications";
import { getDerivedLocale } from "@shared/utils.locale";
import type { LocaleOfStoreType } from "@beautypie/core";

type StoreType = "us" | "uk";
type LocaleType = "en-GB" | "en-US";

function getStoreId(locale: LocaleType = "en-GB"): StoreType {
  return locale === "en-GB" ? "uk" : "us";
}

const uri: string =
  (typeof window !== "undefined"
    ? process.env.NEXT_PUBLIC_BPAPI_URI
    : process.env.API_URI_INTERNAL ||
      process.env.NEXT_PUBLIC_API_URI_INTERNAL ||
      process.env.NEXT_PUBLIC_BPAPI_URI) || "https://www.beautypie.com/graphql";

// Will feature flag this in the future to test the ability to remove global locale
let locale;

export const getLocale = (): LocaleOfStoreType => {
  return (locale = locale || getDerivedLocale());
};

export const setLocale = (x: string): void => {
  locale = x;
};

const tryParse = <T>(value: string): T | undefined => {
  try {
    return JSON.parse(value);
  } catch {
    return;
  }
};

const getCookies = (defaultContext): Record<string, string> => {
  if (typeof window !== "undefined") {
    return parseCookie(window.document.cookie);
  }

  if (defaultContext.headers?.cookie) {
    return parseCookie(defaultContext.headers.cookie);
  }

  return {};
};

export function createContextLink(): ApolloLink {
  return new ApolloLink((operation, forward) => {
    operation.setContext((defaultContext) => {
      const { headers, userSegment = getUserSegment() } = defaultContext;
      locale = defaultContext.locale || headers?.locale || getLocale();

      const cookies = getCookies(defaultContext);

      const sessionCookie = tryParse<SessionCookie>(cookies.trackingSession);
      const testIdentity = isServer()
        ? getServerCookies()?.get("test_identity")?.value
        : cookie.get("test_identity");

      const csrfToken = cookies[CSRF_TOKEN_COOKIE];

      const ctx = {
        locale,
        storeid: getStoreId(locale as LocaleType),
        "x-bp-anonymousid": getInitialAnonId(),
        "x-bp-user-segment": userSegment,
        "x-bp-sessionid": sessionCookie?.id,
        "x-bp-visit-number": sessionCookie?.count,
        "x-bp-fingerprintid": sessionCookie?.fingerprintId,
        "x-bp-testidentity": testIdentity,
        "x-bp-platform": "web",
        "x-bp-experienceid": isServer()
          ? (defaultContext.experienceId ?? "SSR")
          : getExperienceId(),
        "x-bp-client-version":
          process.env.NEXT_PUBLIC_GIT_COMMIT_SHA || "missing",
        ...(csrfToken && { "x-csrf-token": csrfToken })
      };

      return {
        headers: {
          ...headers,
          ...ctx
        }
      };
    });

    return forward(operation);
  });
}

// Will send mutations via POST and queries via GET
const httpGetLink = createHttpLink({
  uri,
  useGETForQueries: true,
  credentials: "include"
});

// Will send all requests via POST - useful when the query becomes large
// and the generated URL exceeds the cloudfront max length
const httpPostLink = createHttpLink({
  uri,
  useGETForQueries: false,
  credentials: "include"
});

const splitLink = split(
  // split based on operation type
  ({ getContext }) => {
    return !!getContext().usePostForQuery;
  },
  httpPostLink,
  httpGetLink
);

// Any error codes not in this list will be silently ignored to
// maintain the current behaviour
const supportedErrorCodes = [
  "BASKET_SERVICE_UNEXPECTED_ERROR",
  "BASKET_SERVICE_BAD_REQUEST_ERROR"
];

const oneMinute = 1000 * 60;

const getRefreshAfter = (): number => {
  if (isServer()) return -1;

  const refreshAfter = window.localStorage.getItem("refreshAfter");

  return refreshAfter ? Number(refreshAfter) : -1;
};

export const accessTokenHasExpired = (): boolean => {
  const refreshAfter = getRefreshAfter();

  // Allow a minute buffer to accomodate clock skew
  if (refreshAfter - oneMinute >= Date.now()) {
    return false;
  }

  return true;
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  const notifications = (graphQLErrors || [])
    .filter(({ extensions }) =>
      supportedErrorCodes.includes(extensions?.code || "MISSING_ERROR_CODE")
    )
    .map(({ extensions }) => {
      return {
        code: extensions?.code,
        handler: "global" as const,
        level: extensions?.alertLevel || ("error" as const),
        params: {}
      };
    });

  if (notifications.length) {
    setNotifications(notifications);
  }
  if (networkError)
    console.error(
      `[Network error]: ${networkError} in operation ${operation.operationName}`
    );
});

const refreshWhenExpiredLink = setContext(
  async (_request, context: DefaultContext & Partial<ApolloReqContext>) => {
    const strategy = extractSessionStrategy(context.sessionStrategy);

    if (strategy === "NONE") {
      return context;
    }

    // Allow a minute buffer to accomodate clock skew
    if (!accessTokenHasExpired()) {
      return context;
    }

    try {
      await refreshToken();

      return context;
    } catch (err) {
      return context;
    }
  }
);

const promiseToObservable = <T>(fn: () => Promise<T>) => {
  return new Observable<T>((observer) => {
    fn().then(
      (value) => {
        if (observer.closed) return;
        observer.next(value);
        observer.complete();
      },
      (err) => observer.error(err)
    );
  });
};

const SESSION_ERRORS = [
  "REQUIRES_AUTHORIZATION",
  "TOKEN_INVALID_ERROR",
  "CSRF_TOKEN_INVALID",
  "CSRF_TOKEN_MISSING"
];

const ACTIONABLE_SESSION_ERRORS = ["MISSING_SESSION", "LOGGED_OUT_OF_REMOTE"];

type SessionStrategy = "REDIRECT_TO_LOGIN" | "START_NEW_ANONYMOUS" | "NONE";
const ensureSession = (
  sessionStrategy: SessionStrategy
): Promise<{ success: boolean }> => {
  return refreshToken()
    .then(() => {
      // Refreshing the token succeeded, retry the original request
      return { success: true };
    })
    .catch((err) => {
      // Refreshing the token failed, check whether the error is fixable
      // by taking a session action
      const codes = getErrorCodes(err.graphQLErrors);

      // If the server returns that there is no session (based on lack of refresh token)
      if (codes.some((code) => ACTIONABLE_SESSION_ERRORS.includes(code))) {
        // The token refresh failed with a fixable error, start a new anonymous session
        if (sessionStrategy === "START_NEW_ANONYMOUS") {
          return (
            anonymousLogin()
              // Anonymous login succeeded, retry the original request
              .then(() => {
                return { success: true };
              })
              .catch(() => {
                return { success: false };
              })
          );
        }

        // The token refresh failed with a fixable error, redirect the user to the
        // login page to initiate a new authenticated session
        if (sessionStrategy === "REDIRECT_TO_LOGIN") {
          clearUser();
          setUserSegment("nonmember");
          navigateTo(
            `/login?redirectTo=${encodeURIComponent(
              `${window.location.pathname}${window.location.search}`
            )}&sessionExpired=true`
          );

          return { success: false };
        }

        // Technically unreachable given the enum of sessionStrategy
        // but needed for consistent typing
        return { success: false };
      }

      // Refresh token failed with an unfixable error
      throw err;
    });
};

const extractSessionStrategy = (
  sessionStrategy?: SessionStrategy | ((isLoggedIn: boolean) => SessionStrategy)
) => {
  if (!sessionStrategy) return "NONE";

  const user = userVar();

  return typeof sessionStrategy === "function"
    ? sessionStrategy(user.isLoggedIn)
    : sessionStrategy;
};

const ensureSessionLink = onError(
  ({ graphQLErrors = [], operation, forward }) => {
    const { sessionStrategy } = operation.getContext() as DefaultContext &
      Partial<ApolloReqContext>;
    const strategy = extractSessionStrategy(sessionStrategy);

    if (strategy === "NONE") {
      return;
    }

    if (containsSomeOfErrors(SESSION_ERRORS, graphQLErrors)) {
      return promiseToObservable(() => ensureSession(strategy)).flatMap(
        (result) => {
          if (result?.success === true) {
            operation.setContext((defaultContext) => {
              const { headers } = defaultContext;
              const csrfToken = cookie.get(CSRF_TOKEN_COOKIE);

              return {
                headers: {
                  ...headers,
                  ...(csrfToken && { "x-csrf-token": csrfToken })
                }
              };
            });

            return forward(operation);
          }

          return new Observable((observer) =>
            observer.error("Unable to refresh session")
          );
        }
      );
    }
  }
);

const notificationsLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    Object.values(response.data || {}).forEach((value) => {
      if (!!value && typeof value === "object" && "notifications" in value) {
        setNotifications(value.notifications);
      }
    });

    return response;
  });
});

const ensureFingerprintIdLink = new ApolloLink((operation, forward) => {
  const { waitForFingerPrint, headers } = operation.getContext();

  if (waitForFingerPrint && !headers["x-bp-fingerprintid"]) {
    //wait fingerprint first and then the original request
    return promiseToObservable(() =>
      getBPFingerprint().catch(() => undefined)
    ).flatMap((result) => {
      operation.setContext(
        (defaultContext: DefaultContext & Partial<ApolloReqContext>) => {
          return {
            headers: {
              ...defaultContext.headers,
              "x-bp-fingerprintid": result?.fingerprintId
            }
          };
        }
      );

      return forward(operation);
    });
  }

  return forward(operation);
});

type ApolloReqContext = {
  sessionStrategy: SessionStrategy | ((isLoggedIn: boolean) => SessionStrategy);
  usePostForQuery?: boolean;
  waitForFingerPrint?: boolean;
};

// This is a bit pointless but it allows us to be more typesafe about the data we
// pass on the otherwise untyped ApolloClient context
export const createRequestContext = ({
  sessionStrategy,
  usePostForQuery = false,
  waitForFingerPrint = false
}: ApolloReqContext): ApolloReqContext => ({
  sessionStrategy,
  usePostForQuery,
  waitForFingerPrint
});

let client: ApolloClient<unknown> | null = null;

export const getClient = (): ApolloClient<unknown> => {
  if (!client || typeof window === "undefined") {
    client = new ApolloClient({
      ssrMode: typeof window === "undefined",
      cache: new InMemoryCache(),
      link: from([
        createContextLink(),
        notificationsLink,
        ensureSessionLink,
        errorLink,
        refreshWhenExpiredLink,
        ensureFingerprintIdLink
      ]).concat(splitLink),
      defaultOptions: {
        query: {
          fetchPolicy: "no-cache"
        }
      },
      connectToDevTools: process.env.NODE_ENV === "development"
    });
  }
  return client;
};

export function toCacheId(typename: string, objId: string): string | undefined {
  return getClient().cache.identify({ __typename: typename, id: objId });
}
